// src/components/layout/Sidebar.jsx
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Dialog,
  Transition,
  Menu,
  Switch,
  MenuButton,
  MenuItems,
  MenuItem,
  TransitionChild,
  DialogPanel,
} from "@headlessui/react";
import {
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronUpIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";

import { HierarchyIcon, DocumentIcon, CalendarIcon } from "../icons";
import { BrandLogo } from "../logo";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import clsx from "clsx";

// IMPORTANTE: servicio de auth
import { logout as apiLogout } from "../../services/authentication.service";

// Admin sidebar items
const adminItems = [
  { id: "organization", label: "Organización", icon: HierarchyIcon, path: "/organization" },
  { id: "question-bank", label: "Banco de preguntas", icon: DocumentIcon, path: "/question-bank" },
  { id: "questionnaire", label: "Cuestionarios", icon: CalendarIcon, path: "/questionnaire" },
];

// User sidebar items
const appItems = [
  { id: "assignments", label: "Mis asignaciones", icon: DocumentIcon, path: "/assignments" },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const { roles, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Mientras AuthContext sigue cargando /users/me, no pintamos sidebar
  if (loading) {
    return null;
  }

  const isAdmin = Array.isArray(roles) && roles.includes("ADMIN");
  const sidebarItems = isAdmin ? [...adminItems, ...appItems] : appItems;

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      onClose && onClose();
      await apiLogout(); // limpia access token y hace POST /auth/logout (cookie HttpOnly)
    } catch (_) {
      // ignorar; igual redirigimos
    } finally {
      setIsLoggingOut(false);
      navigate("/login", { replace: true });
      // opcional: window.location.reload();
    }
  };

  const handleViewProfile = () => {
    navigate("/profile");
  };

  const SidebarContent = () => (
    <div className="teamassess-sidebar">
      {/* Logo */}
      <div className="teamassess-sidebar-header">
        <BrandLogo />
        <button
          type="button"
          className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          onClick={onClose}
          aria-label="Cerrar menú"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {sidebarItems.map((item) => (
            <li key={item.id}>
              <NavLink
                to={item.path}
                onClick={() => onClose && onClose()}
                className={({ isActive }) =>
                  clsx(
                    "teamassess-nav-link",
                    isActive && "is-active"
                  )
                }
              >
                {item.icon && <item.icon aria-hidden="true" />}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Dark Mode Toggle */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all duration-200">
          <div className="flex items-center gap-3">
            {theme === "dark" ? (
              <MoonIcon className="w-5 h-5 text-secondary dark:text-secondary transition-colors duration-200" />
            ) : (
              <SunIcon className="w-5 h-5 text-secondary dark:text-secondary transition-colors duration-200" />
            )}
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {theme === "dark" ? "Modo oscuro" : "Modo claro"}
            </span>
          </div>
          <Switch
            checked={theme === "dark"}
            onChange={toggleTheme}
            className={clsx(
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 dark:focus:ring-offset-gray-800",
              theme === "dark" ? "bg-secondary" : "bg-gray-300"
            )}
          >
            <span className="sr-only">Cambiar tema</span>
            <span
              className={clsx(
                "inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200",
                theme === "dark" ? "translate-x-6" : "translate-x-1"
              )}
            />
          </Switch>
        </div>
      </div>

      {/* User Profile Menu */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center justify-between w-full px-4 py-3 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all duration-200 group">
            <div className="flex items-center gap-3">
              <UserCircleIcon className="w-5 h-5 text-secondary dark:text-secondary transition-colors duration-200" />
              <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-200">
                Perfil
              </span>
            </div>
            <ChevronUpIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors duration-200" />
          </MenuButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute bottom-full left-0 right-0 mb-2 mx-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none overflow-hidden">
              <div className="py-1">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={handleViewProfile}
                      className={clsx(
                        "flex items-center gap-3 w-full px-4 py-3 text-sm text-left transition-colors duration-150",
                        focus
                          ? "bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white"
                          : "text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <UserCircleIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <span>Ver perfil</span>
                    </button>
                  )}
                </MenuItem>

                <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />

                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className={clsx(
                        "flex items-center gap-3 w-full px-4 py-3 text-sm text-left transition-colors duration-150",
                        focus
                          ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          : "text-red-600 dark:text-red-500",
                        isLoggingOut && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      <span>{isLoggingOut ? "Cerrando sesión..." : "Cerrar sesión"}</span>
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 shrink-0 border-r border-gray-200 dark:border-gray-700 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile/Tablet Sidebar */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/90 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative mr-16 flex w-80 max-w-[90vw] flex-1">
                <div className="flex grow flex-col border-r border-gray-200 dark:border-gray-700 shadow-2xl">
                  <SidebarContent />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Sidebar;
